import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';

/**
 * This axios instance will cache responses in localStorage for 30 seconds.
 * It works by intercepting requests and responses and storing the response data in localStorage.
 * If a request is made and there is a cached response in localStorage that is less than 30 seconds old,
 * the request interceptor will set the cached data on the request, the `cached` header to true and reject the request.
 * This will trigger the response error handler, which will return the cached response data as a resolved promise.
 */

const TTL = 1000 * 30; // 30 seconds

interface CacheError extends Error {
  headers?: {
    cached?: boolean;
  };
}

// Create an Axios instance
const cachedAxios = axios.create();

// This converts the rejection into a resolved promise if cached data was returned
function errorHandler(error: CacheError) {
  if (error.headers?.cached === true) {
    return Promise.resolve(error);
  }
  return Promise.reject(error);
}

function responseHandler<T>(response: AxiosResponse<T>): AxiosResponse<T> {
  const cacheKey = `${response.config.url}_${JSON.stringify(response.config.params)}`;

  // Store the response data in localStorage along with the current time
  const cacheValue = {
    data: response.data,
    timestamp: new Date().getTime(),
  };
  localStorage.setItem(cacheKey, JSON.stringify(cacheValue));

  return response;
}

function requestHandler(request: AxiosRequestConfig) {
  const cacheKey = `${request.url}_${JSON.stringify(request.params)}`;

  // Retrieve the cached value from localStorage
  const cachedValue = localStorage.getItem(cacheKey);

  if (cachedValue) {
    const { data, timestamp } = JSON.parse(cachedValue);

    // Check if the cache is still valid )
    if (new Date().getTime() - timestamp < TTL) {
      request.headers.cached = true;
      request.data = data;
      // Return the cached value as a fulfilled Promise
      return Promise.reject(request);
    }
    // Remove the expired cache
    localStorage.removeItem(cacheKey);
  }
  // Return a promise that resolves to the original config object
  return request;
}

cachedAxios.interceptors.request.use((request) => requestHandler(request));
cachedAxios.interceptors.response.use(
  (response) => responseHandler(response),
  (error) => errorHandler(error),
);

export { errorHandler, responseHandler, requestHandler };

export default cachedAxios;
